import spacetime from 'spacetime';
import { ascend, compose, prop, sort } from 'ramda';
import { useDispatch } from 'react-redux';
import { ChangeEvent, Dispatch, useCallback, useEffect, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  addEndCheckingErp,
  changeEndCheckingErp,
  deleteEndCheckingErp,
  fetchEndCheckingErpListAll,
} from 'APIPackage/SystemInoperabilityModule/services/endCheckingErpApi';
import {
  IEndCheckingErp,
  IEndCheckingErpForm,
  IEndCheckingErpItemForm,
} from 'APIPackage/SystemInoperabilityModule/interfaces/IEndCheckingErp';
import { TitleWithAddButton } from 'UIPackage/MainModule/components/TitleWithAddButton';
import { Row } from 'UIPackage/FormModule/components/Row';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { Button } from 'UIPackage/MainModule/components/Button';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { fetchKnmSupervisionTypesAllWithFederalLaw } from 'APIPackage/AdminModule/services/adminKnmSupervisionTypesApi';
import { IKnmSupervisionType } from 'APIPackage/CatalogsModule/interfaces/IKnmSupervisionType';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import {
  END_CHECKING_EPR_ERROR_CHANGE,
  END_CHECKING_EPR_SUCCESS_CHANGE,
} from 'CorePackage/MainModule/constants/notices';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { ISupervisionTypeSelectOption } from 'CorePackage/CatalogsModule/hooks/useKnmSupervisionTypes';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';

import styles from './EndCheckingErp.module.scss';

export const EndCheckingErp = () => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [dictionaryValues, setDictionaryValues] = useState<IKnmSupervisionType[]>([]);
  const [settings, setSettings] = useState<IEndCheckingErpItemForm[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Список вариантов для селекта
   */
  const selectOptions = useCallback(
    (initList?: IKnmSupervisionType[]) => {
      return (initList || dictionaryValues)
        .filter(i => i.id !== null && i.federalLaw?.code === '294')
        .map(item => {
          return {
            ...item,
            label: `${item.digitCode || ''} ${item.federalLaw?.name || ''} ${item.name}`,
            value: item.id,
          };
        }) as ISupervisionTypeSelectOption[];
    },
    [dictionaryValues],
  );

  /**
   * Сам запрос на получение списка значений справочника и списка настроек
   */
  const getData = useCallback(async () => {
    const settingsList = await fetchEndCheckingErpListAll();

    const dictionaryValuesList: IKnmSupervisionType[] = await fetchKnmSupervisionTypesAllWithFederalLaw();

    const byAge = ascend(compose(prop('id')));
    const elementsById = sort<IKnmSupervisionType>(byAge, dictionaryValuesList);

    setDictionaryValues(elementsById);

    const options = selectOptions(elementsById);

    setSettings(
      settingsList.map(i => {
        return {
          ...i,
          supervisionTypeIds: options.filter(j => i.supervisionTypeIds?.find(k => k === j.id) !== undefined),
          fromDate: formatDate({
            date: i.fromDate || '',
            parseFormat: SYSTEM_DATE_FORMAT_FNS,
            dateFormat: DATE_FORMAT_LONG_FNS,
          }),
          toDate: formatDate({
            date: i.toDate || '',
            parseFormat: SYSTEM_DATE_FORMAT_FNS,
            dateFormat: DATE_FORMAT_LONG_FNS,
          }),
        };
      }),
    );
  }, []);

  /**
   * Делаем запрос на получение содержимого справочника supervision_type
   */
  useEffect(() => {
    getData();
  }, []);

  /**
   * Функция валидации перед сохранением
   */
  const validateForm = useCallback((values: IEndCheckingErpForm) => {
    const validateDatePicker = (fromDate: string | undefined, toDate: string | undefined, startDate?: boolean) => {
      if (
        toDate &&
        fromDate &&
        (!formatDate({ date: toDate || '', dateFormat: DATE_FORMAT_LONG_FNS }) ||
          !formatDate({ date: fromDate || '', dateFormat: DATE_FORMAT_LONG_FNS }))
      ) {
        return 'Некорректный период';
      }
      if (startDate && toDate && fromDate) {
        return makeNoLaterThenValidator(
          'Дата начала периода должна быть меньше или равна дате окончания',

          () => toDate,
        )(fromDate);
      }

      if (fromDate && toDate) {
        return makeNoSoonerThenValidator(
          'Дата окончания периода должна быть больше или равна дате начала',

          () => fromDate,
        )(toDate);
      }

      if ((toDate && !fromDate && startDate) || (fromDate && !toDate && !startDate)) return 'Поле не может быть пустым';
    };

    return {
      items: values.items?.map(setting => {
        return {
          supervisionTypeIds:
            !setting.all && !setting.supervisionTypeIds ? 'Выберите виды контроля или отметьте все' : undefined,
          fromDate: !setting.fromDate
            ? 'Обязательно к заполнению'
            : validateDatePicker(setting.fromDate ?? undefined, setting.toDate ?? undefined, true),
          toDate: !setting.toDate
            ? 'Обязательно к заполнению'
            : validateDatePicker(setting.fromDate ?? undefined, setting.toDate ?? undefined, true),
        };
      }),
    };
  }, []);

  /**
   * Функция сохранения всех изменений
   */
  const onSave = useCallback((values: IEndCheckingErpForm) => {
    setIsSaving(true);
    const promises: Promise<void>[] = [];

    values.items.forEach(setting => {
      const settingForApi: IEndCheckingErp = {
        ...setting,
        supervisionTypeIds: setting.supervisionTypeIds?.map(i => i.id) || null,
        fromDate: formatDate({ date: setting.fromDate || '', dateFormat: DATE_FORMAT_ISO_FNS }),
        toDate: formatDate({ date: setting.toDate || '', dateFormat: DATE_FORMAT_ISO_FNS }),
      };

      if (setting.id) {
        promises.push(changeEndCheckingErp(settingForApi));
      } else {
        promises.push(addEndCheckingErp(settingForApi));
      }
    });

    Promise.all(promises)
      .then(() => {
        getData();
        setIsSaving(false);
        dispatch(
          addNoticeAction({
            noticeType: NoticeTypesEnum.SUCCESS,
            messageType: MessageTypesEnum.ADMIN_ERP_CHECKING,
            renderContent: END_CHECKING_EPR_SUCCESS_CHANGE,
          }),
        );
      })
      .catch(() => {
        getData();
        setIsSaving(false);
        dispatch(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADMIN_ERP_CHECKING,
            renderContent: END_CHECKING_EPR_ERROR_CHANGE,
          }),
        );
      });
  }, []);

  const renderForm = useCallback(
    ({ handleSubmit, form }: FormRenderProps<IEndCheckingErpForm>) => {
      const { push, remove } = form.mutators;
      const onAddSettings = useCallback(() => {
        const newSetting: IEndCheckingErpItemForm = {
          all: false,
          supervisionTypeIds: null,
          enabled: true,
          fromDate: null,
          toDate: null,
          id: null,
        };

        push('items', newSetting);
      }, []);

      const removeItem = useCallback(
        (id: number | null, index: number) => () => {
          if (id) {
            deleteEndCheckingErp(id).then(getData);
          } else {
            remove('items', index);
          }
        },
        [settings, getData],
      );

      /**
       * Функция изменения галочки "Все виды контроля (надзора)"
       */
      const onChangeAll = useCallback(
        (e: ChangeEvent<HTMLInputElement>, index: number) => {
          if (e.target.checked) {
            form.change(`items[${index}].supervisionTypeIds` as keyof IEndCheckingErpForm, []);
          }
        },
        [form],
      );

      return (
        <Row>
          <div className={styles.TitleRow}>
            <TitleWithAddButton text="Даты проведения проверок для завершения в рамках КНМ" onClick={onAddSettings} />
          </div>
          <form onSubmit={handleSubmit} id="end-check-erp">
            <FieldArray name="items">
              {({ fields }: FieldArrayRenderProps<IEndCheckingErp, HTMLElement>) => {
                if (!fields.length) {
                  return null;
                }

                return fields.map((name, index) => {
                  const fieldValue: IEndCheckingErp = fields.value?.[index];

                  return (
                    <div className={styles.WrapperRow} key={index}>
                      <span>{index + 1}.</span>
                      <div className={styles.SelectWrapper}>
                        <SelectInputField
                          name={`${name}.supervisionTypeIds`}
                          className={styles.SelectInput}
                          options={selectOptions()}
                          disabled={fieldValue.all}
                          isClearable={false}
                          isMulti={true}
                          isSearchable={true}
                          isMultiLine
                        />
                        <div className={styles.CheckboxWrapper}>
                          <CheckboxField
                            id={`${name}.all`}
                            name={`${name}.all`}
                            type={'checkbox'}
                            onChangeCallback={e => onChangeAll(e, index)}
                            label="Все виды контроля (надзора)"
                            theme="system"
                          />
                          <CheckboxField
                            type={'checkbox'}
                            id={`${name}.enabled`}
                            name={`${name}.enabled`}
                            label="Активно"
                            theme="system"
                          />
                        </div>
                      </div>
                      <div className={styles.DatesWrapper}>
                        <DatePickerField
                          className={`shared-row-field`}
                          id={`${name}.fromDate`}
                          name={`${name}.fromDate`}
                          maxDate={
                            !fieldValue?.toDate
                              ? undefined
                              : spacetime(
                                  formatDate({ date: fieldValue.toDate || '', dateFormat: DATE_FORMAT_ISO_FNS }),
                                ).toNativeDate()
                          }
                        />
                        <span>-</span>
                        <DatePickerField
                          className={`shared-row-field`}
                          id={`${name}.toDate`}
                          name={`${name}.toDate`}
                          minDate={
                            !fieldValue?.fromDate
                              ? undefined
                              : spacetime(
                                  formatDate({ date: fieldValue.fromDate || '', dateFormat: DATE_FORMAT_ISO_FNS }),
                                ).toNativeDate()
                          }
                          maxDate={spacetime().toNativeDate()}
                        />
                      </div>
                      <CloseButton className={styles.CloseButton} onClick={removeItem(fieldValue.id, index)} />
                    </div>
                  );
                });
              }}
            </FieldArray>

            <Row>
              <Button type="submit" color="primary" size="medium" text="Применить" disabled={isSaving} />
            </Row>
          </form>
        </Row>
      );
    },
    [settings, getData],
  );

  return (
    <Form<IEndCheckingErpForm>
      initialValues={{ items: settings }}
      render={renderForm}
      onSubmit={onSave}
      validate={validateForm}
      mutators={{ ...arrayMutators }}
    />
  );
};
